import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  BorderRadius,
  Link,
  chakra,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { eclColor } from '~/pages/AdminApprovalFlow/constants/eclColor'
import { routePaths } from '~/routes/routePaths'

/**
 * design `.apr-autobid-spec` の key-value 表 (発注方向 / カバー率 / 価格)。
 *
 * 用語は「カバー率」。 「補填率」との揺れは PdM が 2026-08-13 に **カバー率へ統一**と
 * 決定済み (`docs/specs/bidding/spot-bidding.md` の Discussion 表 / issue #5165)。
 *
 * [design 差異] design は値の中の「不足」「余剰」「指値」「成行」を太字 /
 * `.apr-autobid-mode-tag` で強調しているが、実装は 1 行 1 キーの素のテキストに
 * している。強調のために文を分割すると i18n キーが文の断片になり、語順の違う
 * 言語で組み立て直せなくなるため。情報そのものは同じ。
 */
const autoBidSpecRows = (
  isIntraday: boolean,
): ReadonlyArray<{ labelKey: string; valueKey: string }> => [
  {
    labelKey: 'page:adminApprovalFlow.autoBid.spec.directionLabel',
    valueKey: 'page:adminApprovalFlow.autoBid.spec.directionValue',
  },
  {
    labelKey: 'page:adminApprovalFlow.autoBid.spec.coverageLabel',
    valueKey: 'page:adminApprovalFlow.autoBid.spec.coverageValue',
  },
  {
    labelKey: 'page:adminApprovalFlow.autoBid.spec.priceLabel',
    // 価格の選択肢は業務処理で違う。 スポットは 指値 / 成行 (AC-APF-041)、
    // 時間前は 価格を決める / 価格を決めない (食いつき) (AC-APF-042) で、
    // **時間前に成行は無い**。 分岐前は spot の文言を時間前でも出していたため、
    // 凡例に実在しない選択肢が書かれていた (#7969)。
    valueKey: isIntraday
      ? 'page:adminApprovalFlow.autoBid.spec.priceValueIntraday'
      : 'page:adminApprovalFlow.autoBid.spec.priceValue',
  },
]

/**
 * design `.apr-autobid-spec`: 外枠 border + radius を効かせるため
 * `border-collapse: separate` + `overflow: hidden` を使う
 * (`collapse` だと角丸がセルの直線に負ける)。
 */
const specTableStyle: React.CSSProperties = {
  width: '100%',
  margin: 0,
  fontSize: fontSizes.md.value,
  border: `1px solid ${eclColor.borderLow}`,
  borderRadius: BorderRadius.radiiBase.value,
  borderCollapse: 'separate',
  borderSpacing: 0,
  overflow: 'hidden',
}

const specCellStyle: React.CSSProperties = {
  textAlign: 'left',
  verticalAlign: 'top',
  padding: '8px 12px',
  borderBottom: `1px solid ${eclColor.borderLow}`,
  lineHeight: 1.6,
}

/** 最終行は外枠の border と二重線になるので下線を持たない */
const lastSpecRowStyle: React.CSSProperties = { borderBottom: 'none' }

type Props = {
  /** 承認モード = 自動 のエリアが 1 件以上あるか (AC-APF-036 の空メッセージ分岐)。 */
  hasAutoAreas: boolean
  /**
   * 時間前入札のセクションか。 説明表の「価格」行の選択肢が業務処理で違うため
   * (スポット = 指値 / 成行、 時間前 = 価格を決める / 食いつき)。
   * design `admin/approval-flow.html` も `p.key === 'intraday-bid'` で出し分ける。
   */
  isIntraday: boolean
}

/**
 * 自動入札ルールの **説明パネル** (AC-APF-035/036)。承認マトリクスの直下に置く。
 *
 * エリア別のルール要約と「ルール編集」ボタンは **マトリクスの「自動」セル内**が正
 * (AC-APF-037「承認マトリクスの各「自動」セル内に、現行ルールの 2 行サマリ
 * (買い行・売り行) と「ルール編集」ボタンを表示する」、design
 * `.apr-autobid-cellsum`)。「全エリア一括編集」も、マトリクスの一括列 / 一括
 * セレクト脇のガイド行が持つ。**このパネルは「自動とは何をするのか」だけを担い、
 * 操作を持たない**。
 *
 * 設定 CRUD のみがこのスライスの scope で、発注実行は #2511。
 */
export const AutoBidPanel = ({
  hasAutoAreas,
  isIntraday,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const specRows = autoBidSpecRows(isIntraday)

  return (
    <div
      style={{
        padding: '16px',
        borderTop: `1px solid ${eclColor.borderLow}`,
        background: eclColor.surfacePrimary,
      }}
    >
      <div
        style={{
          marginBottom: '8px',
          fontWeight: 600,
          fontSize: fontSizes.lg.value,
          color: eclColor.textHigh,
        }}
      >
        {t('page:adminApprovalFlow.autoBid.title')}
      </div>

      {/* AC-APF-035: bid-limits 優先注記は自動エリアの有無に関わらず常時表示。
          常設の説明は Alert にせず本文テキストで出す (UI 辞書 alert-placement.md §0)。
          導線は文末の inline `Link` (内部リンクなので同じタブ。 link-target.md)。
          spec approval-flow.md §3 (issue #5439、2026-08-18 PdM 判断): 「対象コマが
          取引中断中 (JEPX SuspenseCode 由来) でも自動入札ルールは通常通り発火し
          入札を行う」旨も補足する。「中断中」は発注が止まると読まれやすい語なので、
          止まらないことを明示する側に倒す。 */}
      {/* AC-APF-035 は入札制限の優先と自動調整の 2 文を「常時表示」と定めるので、 ? の tooltip に
          隠さず本文に出す (文言は spec 付録。 React mock の limitsNote も本文)。 取引中断中の補足も文で出す */}
      <chakra.p textStyle="body" marginBottom="12px" color="Text.highEmphasis">
        <span>{t('page:adminApprovalFlow.autoBid.headerHint')}</span>{' '}
        <span>{t('page:adminApprovalFlow.autoBid.headerHintDetail')}</span>{' '}
        <span>{t('page:adminApprovalFlow.autoBid.headerHintSuspended')}</span>{' '}
        <Link asChild>
          <RouterLink to={routePaths.adminBidLimits.path}>
            {t('page:adminApprovalFlow.autoBid.bidLimitsLink')}
          </RouterLink>
        </Link>
      </chakra.p>

      {/* design `.apr-autobid-desc`: 「自動」が何をするのかの説明。承認モードを
          自動にする前に読む情報なので、自動エリアの有無に関わらず常時出す。 */}
      <p
        style={{
          margin: '0 0 8px',
          fontSize: fontSizes.md.value,
          lineHeight: 1.6,
          color: eclColor.textHigh,
        }}
      >
        {t('page:adminApprovalFlow.autoBid.lead')}
      </p>
      <table style={specTableStyle}>
        <tbody>
          {specRows.map(({ labelKey, valueKey }, index) => (
            <tr key={labelKey}>
              <th
                scope="row"
                style={{
                  ...specCellStyle,
                  ...(index === specRows.length - 1 ? lastSpecRowStyle : {}),
                  width: '1%',
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                  color: eclColor.textHigh,
                }}
              >
                {t(labelKey)}
              </th>
              <td
                style={{
                  ...specCellStyle,
                  ...(index === specRows.length - 1 ? lastSpecRowStyle : {}),
                  color: eclColor.textMid,
                }}
              >
                {t(valueKey)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* AC-APF-036: 自動エリアが 0 件の時の空メッセージ。ルール要約はセル内に
          出るので (AC-APF-037)、このパネルが持つ状態依存の表示はここだけ。 */}
      {!hasAutoAreas && (
        <div
          style={{
            marginTop: '12px',
            padding: '16px',
            textAlign: 'center',
            color: eclColor.textMid,
            fontSize: fontSizes.sm.value,
            background: eclColor.surfaceSecondary,
          }}
        >
          {t('page:adminApprovalFlow.autoBid.empty')}
        </div>
      )}
    </div>
  )
}
